/**
 * Tour Controller - Manages app tour state and navigation menu control
 * Uses a simple event-based approach for cross-component communication
 * 
 * CRITICAL: All state is MODULE-LEVEL to ensure consistency across all hook instances
 * 
 * DURABLE FLAGS:
 * - therappi_conversational_onboarding_completed: Set TRUE when onboarding completes (never auto-reset)
 * - therappi_conversational_onboarding_just_completed: One-time trigger for tour auto-start
 * - therappi-tour-requested: Alternative trigger from "Show Me Around" button
 * 
 * Tour auto-start requires:
 * 1. hasUser === true
 * 2. conversational_onboarding_completed === true
 * 3. tourSeen === false
 * 4. (just_completed === true OR tourRequested === true)
 */

type TourEventListener = (action: string) => void;

// localStorage keys
const TOUR_REQUESTED_KEY = 'therappi-tour-requested';
const CONVERSATIONAL_COMPLETED_KEY = 'therappi_conversational_onboarding_completed';
const CONVERSATIONAL_JUST_COMPLETED_KEY = 'therappi_conversational_onboarding_just_completed';
const TOUR_SEEN_KEY = 'therappi-first-time-tour-seen';

// GLOBAL MODULE-LEVEL STATE - shared across ALL useTourController instances
let _isTourActive = false;
let _isTourPrepared = false;
let _onboardingJustCompleted = false;
let _currentTourStep = 0;

// GLOBAL WINDOW FLAG - for components that can't import the hook
declare global {
  interface Window {
    __TOUR_ACTIVE__?: boolean;
  }
}

// Sync window flag with module state
function syncWindowTourFlag() {
  try {
    (window as Window).__TOUR_ACTIVE__ = _isTourActive || _isTourPrepared || _onboardingJustCompleted;
  } catch {
    // SSR or other environment where window is not available
  }
}

// Flight recorder logger
let _flightLogger: ((event: string, data: Record<string, unknown>) => void) | null = null;

export function setFlightLogger(logger: (event: string, data: Record<string, unknown>) => void) {
  _flightLogger = logger;
}

function logTour(event: string, data: Record<string, unknown> = {}) {
  const logData = {
    ...data,
    tourActive: _isTourActive,
    tourPrepared: _isTourPrepared,
    onboardingJustCompleted: _onboardingJustCompleted,
    step: _currentTourStep,
  };
  
  console.log(`[TourController] ${event}`, logData);
  
  if (_flightLogger) {
    _flightLogger(`TOUR_${event}`, logData);
  }
}

// STABLE SINGLETON: Event emitter - listeners persist across re-renders
const _listeners = new Set<TourEventListener>();

const tourEvents = {
  emit(action: string) {
    logTour('EVENT_EMIT', { action, listenerCount: _listeners.size });
    _listeners.forEach(listener => {
      try {
        listener(action);
      } catch (e) {
        console.error('[TourController] Listener error:', e);
      }
    });
  },
  subscribe(listener: TourEventListener) {
    // Prevent duplicate subscriptions
    if (_listeners.has(listener)) {
      logTour('SUBSCRIBE_SKIPPED_DUPLICATE', { listenerCount: _listeners.size });
      return () => {};
    }
    _listeners.add(listener);
    logTour('SUBSCRIBE', { listenerCount: _listeners.size });
    return () => {
      _listeners.delete(listener);
      logTour('UNSUBSCRIBE', { listenerCount: _listeners.size });
    };
  }
};

// ============= DURABLE FLAG FUNCTIONS =============

// DB completion state - synced from useOnboardingStateMachine
let _onboardingCompletedDB: boolean = false;

export const setOnboardingCompletedDB = (completed: boolean): void => {
  _onboardingCompletedDB = completed;
  // Keep localStorage in sync when DB says completed (offline stability)
  if (completed) {
    try {
      localStorage.setItem(CONVERSATIONAL_COMPLETED_KEY, 'true');
    } catch {}
  }
};

// Check if conversational onboarding was ever completed
// RECONCILED: Returns true if EITHER localStorage OR DB says completed
export const isConversationalCompleted = (): boolean => {
  try {
    const localStorageCompleted = localStorage.getItem(CONVERSATIONAL_COMPLETED_KEY) === 'true';
    const completed = localStorageCompleted || _onboardingCompletedDB;
    
    // Keep localStorage in sync if DB says completed (prevents race conditions)
    if (_onboardingCompletedDB && !localStorageCompleted) {
      localStorage.setItem(CONVERSATIONAL_COMPLETED_KEY, 'true');
    }
    
    return completed;
  } catch {
    return _onboardingCompletedDB;
  }
};

// Check if conversational onboarding JUST completed (one-time trigger)
export const isConversationalJustCompleted = (): boolean => {
  try {
    return localStorage.getItem(CONVERSATIONAL_JUST_COMPLETED_KEY) === 'true';
  } catch {
    return false;
  }
};

// Mark conversational onboarding as completed (sets BOTH flags)
export const markConversationalCompleted = (): void => {
  try {
    localStorage.setItem(CONVERSATIONAL_COMPLETED_KEY, 'true');
    localStorage.setItem(CONVERSATIONAL_JUST_COMPLETED_KEY, 'true');
    _onboardingJustCompleted = true;
    syncWindowTourFlag();
    logTour('MARK_CONVERSATIONAL_COMPLETED', { 
      completed: true, 
      justCompleted: true 
    });
  } catch {
    // Ignore localStorage errors
  }
};

// Clear the "just completed" flag - call when tour actually starts
export const clearConversationalJustCompleted = (): void => {
  try {
    localStorage.removeItem(CONVERSATIONAL_JUST_COMPLETED_KEY);
    _onboardingJustCompleted = false;
    logTour('CLEAR_CONVERSATIONAL_JUST_COMPLETED', {});
  } catch {
    // Ignore localStorage errors
  }
};

// Check if tour has been seen
export const isTourSeen = (): boolean => {
  try {
    return localStorage.getItem(TOUR_SEEN_KEY) === 'true';
  } catch {
    return false;
  }
};

// Mark tour as seen
export const markTourSeen = (): void => {
  try {
    localStorage.setItem(TOUR_SEEN_KEY, 'true');
    logTour('MARK_TOUR_SEEN', {});
  } catch {
    // Ignore localStorage errors
  }
};

// Check if a tour was requested but not yet started
export const isTourRequested = (): boolean => {
  try {
    return localStorage.getItem(TOUR_REQUESTED_KEY) === 'true';
  } catch {
    return false;
  }
};

// Clear the tour requested flag
export const clearTourRequested = (): void => {
  try {
    localStorage.removeItem(TOUR_REQUESTED_KEY);
  } catch {
    // Ignore localStorage errors
  }
};

// ============= GLOBAL STATE GETTERS =============

// isTourActiveGlobal: Returns true if tour is ACTIVE (running OR being prepared)
export const isTourActiveGlobal = (): boolean => _isTourActive || _isTourPrepared;

// isTourOrTransitioning: Returns true if tour OR in post-onboarding transition
export const isTourOrTransitioning = (): boolean => _isTourActive || _isTourPrepared || _onboardingJustCompleted;

// Cache-bust: 2026-02-15
export const isOnboardingTransitioning = (): boolean => _onboardingJustCompleted;

export const getCurrentTourStep = (): number => _currentTourStep;

// Reset all tour flags to initial state
export const resetTourFlags = (): void => {
  logTour('RESET_TOUR_FLAGS', { 
    prev: { 
      isTourActive: _isTourActive, 
      isTourPrepared: _isTourPrepared, 
      onboardingJustCompleted: _onboardingJustCompleted 
    } 
  });
  _isTourActive = false;
  _isTourPrepared = false;
  _onboardingJustCompleted = false;
  _currentTourStep = 0;
  syncWindowTourFlag();
  clearTourRequested();
};

// ============= TOUR CONTROLLER HOOK =============

export const useTourController = () => {
  return {
    openMenu: () => tourEvents.emit('open-menu'),
    closeMenu: () => tourEvents.emit('close-menu'),
    
    // Mark conversational onboarding as complete (called from ConversationalOnboarding)
    markConversationalComplete: () => {
      logTour('HOOK_MARK_CONVERSATIONAL_COMPLETE', {});
      markConversationalCompleted();
    },
    
    // Prepare tour - sets flags immediately (use BEFORE navigation)
    prepareTour: () => {
      logTour('PREPARE_TOUR', {});
      _isTourPrepared = true;
      _isTourActive = true;
      _onboardingJustCompleted = true;
      syncWindowTourFlag();
      try {
        localStorage.setItem(TOUR_REQUESTED_KEY, 'true');
      } catch {}
    },
    
    // Start tour - clears just_completed immediately to prevent re-trigger
    startTour: () => {
      logTour('START_TOUR', {});
      _isTourActive = true;
      _isTourPrepared = false;
      _currentTourStep = 0;
      syncWindowTourFlag();
      // CRITICAL: Clear the one-time trigger immediately
      clearConversationalJustCompleted();
      clearTourRequested();
      tourEvents.emit('start-tour');
    },
    
    // Update tour step
    setTourStep: (step: number) => {
      _currentTourStep = step;
      logTour('SET_STEP', { step });
    },
    
    // End tour - sets tourSeen and clears all flags
    endTour: () => {
      logTour('END_TOUR', {});
      _isTourActive = false;
      _isTourPrepared = false;
      _onboardingJustCompleted = false;
      _currentTourStep = 0;
      syncWindowTourFlag();
      markTourSeen();
      clearTourRequested();
      clearConversationalJustCompleted();
      tourEvents.emit('end-tour');
    },
    
    // Check if tour is active OR being prepared OR transitioning from onboarding
    isTourActive: () => _isTourActive || _isTourPrepared || _onboardingJustCompleted,
    
    // Check specifically if in transition window
    isTransitioning: () => _onboardingJustCompleted,
    
    // Check if tour was requested (persisted)
    isTourRequested: () => isTourRequested(),
    
    // Check if conversational just completed
    isConversationalJustCompleted: () => isConversationalJustCompleted(),
    
    // Check if conversational ever completed
    isConversationalCompleted: () => isConversationalCompleted(),
    
    // Clear tour requested flag
    clearTourRequested: () => clearTourRequested(),
    
    // Get current step
    getCurrentStep: () => _currentTourStep,
    
    subscribe: (listener: TourEventListener) => tourEvents.subscribe(listener)
  };
};
